import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

// After making an Apple Music playlist from a list, or on trying to make one
// again: where to find it, and a way into Apple Music. Apple gives no link
// that opens a private library playlist (only public ones have links, and
// its API cannot make a playlist public), so this says where it is and
// opens the Music app. Making it again offers to make another, since Apple
// Music cannot update the first. The overlay + centered card pattern of the
// app's other modals.

/** Opens Apple Music: the Music app on Apple devices (plain `music://`; any
 *  deeper path is refused there), else the web player, where the library
 *  link does open the playlist. */
export function appleMusicOpenURL(playlistURL: string | null, userAgent = navigator.userAgent) {
  if (/iPhone|iPad|iPod|Macintosh/.test(userAgent)) return 'music://';
  return playlistURL || 'https://music.apple.com/';
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
`;

const Modal = styled.div`
  background: white;
  color: black;
  border-radius: 16px;
  width: 90%;
  max-width: 320px;
  padding: 1.5rem 1.25rem 1.25rem;
  position: relative;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #999;
  font-size: 1.25rem;
  line-height: 1;
`;

const Title = styled.div`
  font-weight: 800;
  font-size: 1rem;
  margin-bottom: 0.5em;
`;

const Subtitle = styled.div`
  color: #777;
  font-size: 0.85rem;
  line-height: 1.4;
  margin-bottom: 1.25em;
`;

const PrimaryLink = styled.a`
  display: block;
  background: black;
  color: white;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 0.65em 1.5em;
  border-radius: 8px;
  margin-bottom: 0.75em;

  &:hover {
    background: #333;
  }
`;

const SecondaryButton = styled.button`
  display: block;
  width: 100%;
  background: white;
  color: black;
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 0.6em 1.5em;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`;

interface AppleMusicPlaylistDialogProps {
  /** 'created' right after making it; 'existing' when it was made before. */
  kind: 'created' | 'existing';
  savedAt: string | null;
  missingCount?: number;
  playlistURL: string | null;
  onMakeAnother: () => void;
  onClose: () => void;
}

const WHERE =
  "Apple Music doesn't let other apps open a playlist directly. Open Apple Music and look under Playlists or Recently Added.";

export default function AppleMusicPlaylistDialog(props: AppleMusicPlaylistDialogProps) {
  const firstRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Back to whatever had focus (the Apple Music icon) on close.
    const opener = document.activeElement as HTMLElement | null;
    if (firstRef.current) firstRef.current.focus({ preventScroll: true });
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        props.onClose();
        return;
      }
      // Tab cycles within the dialog, as aria-modal promises.
      if (e.key !== 'Tab' || !modalRef.current) return;
      const items = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const when = props.savedAt
    ? ' on ' +
      new Date(props.savedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';
  const missing = props.missingCount || 0;
  const title =
    props.kind === 'created' ? 'Playlist created on Apple Music' : 'Already on Apple Music';
  const text =
    props.kind === 'created'
      ? (missing > 0
          ? (missing === 1 ? "1 song wasn't" : missing + " songs weren't") +
            ' available on Apple Music. '
          : '') +
        WHERE +
        ' It may take a minute to show up.'
      : 'You made a playlist from this list' + when + '. ' + WHERE;
  const openURL = appleMusicOpenURL(props.playlistURL);
  const opensApp = openURL.indexOf('music://') === 0;

  return (
    <Overlay onClick={props.onClose}>
      <Modal
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="apple-music-playlist-title"
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton type="button" onClick={props.onClose} aria-label="Close">
          &times;
        </CloseButton>
        <Title id="apple-music-playlist-title">{title}</Title>
        <Subtitle>{text}</Subtitle>
        <PrimaryLink
          ref={firstRef as any}
          href={openURL}
          // The app opens over the page; only the web player needs a tab.
          target={opensApp ? undefined : '_blank'}
          rel={opensApp ? undefined : 'noopener noreferrer'}
          onClick={() => {
            // After the browser has followed the link: closing in the same
            // click takes the link off the page first.
            setTimeout(props.onClose, 0);
          }}
        >
          Open Apple Music
        </PrimaryLink>
        <SecondaryButton
          type="button"
          onClick={props.kind === 'created' ? props.onClose : props.onMakeAnother}
        >
          {props.kind === 'created' ? 'Done' : 'Make Another'}
        </SecondaryButton>
      </Modal>
    </Overlay>
  );
}
